import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, API_URL } from '../../api/client';
import DashboardLayout from '../../components/DashboardLayout';
import { Skeleton } from '../../components/Skeleton';
import Icon from '../../components/Icon';
import { formatDateTime } from '../../lib/time';

const PLATFORM_LABEL = {
  youtube: 'YouTube', tiktok: 'TikTok', twitch: 'Twitch', kick: 'Kick', instagram: 'Instagram', other: 'Diğer',
};

export default function AdminPublishers() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.adminPublishers().then(setData).finally(() => setLoading(false));
  }, []);

  const shown = useMemo(() => {
    const list = data?.publishers || [];
    if (!q) return list;
    const s = q.toLowerCase();
    return list.filter((p) =>
      (p.displayName || '').toLowerCase().includes(s)
      || (p.fullName || '').toLowerCase().includes(s)
      || (p.email || '').toLowerCase().includes(s)
      || (p.platforms || [p.platform]).join(' ').toLowerCase().includes(s));
  }, [data, q]);

  const S = data?.summary || {};
  const date = (d) => (d ? formatDateTime(d, lang).slice(0, 10) : '—');

  return (
    <DashboardLayout title={t('adminPublishers.title')} heading={false}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--sp-4)', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="page-title" style={{ margin: 0 }}>{t('adminPublishers.title')}</h1>
          <p className="page-sub" style={{ margin: '4px 0 0' }}>{t('adminPublishers.sub')}</p>
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('adminPublishers.search')} style={{ maxWidth: 260 }} />
      </div>

      {loading ? (
        <Skeleton className="sk-block" style={{ marginTop: 20 }} />
      ) : (
        <>
          <div className="growth-summary card">
            <div><span>{t('adminPublishers.sum_publishers')}</span><strong>{S.publishers || 0}</strong></div>
            <div><span>{t('adminPublishers.sum_terms')}</span><strong>{S.termsAccepted || 0}</strong></div>
            <div><span>{t('adminPublishers.sum_links')}</span><strong>{S.links || 0}</strong></div>
            <div><span>{t('adminPublishers.sum_clicks')}</span><strong>{(S.clicks || 0).toLocaleString(lang === 'en' ? 'en-US' : 'tr-TR')}</strong></div>
            <div><span>{t('adminPublishers.sum_referred')}</span><strong>{S.referred || 0}<span style={{ fontSize: '0.9rem', color: 'var(--text-3)', fontWeight: 400 }}> / {S.referredActive || 0} {t('adminPublishers.active')}</span></strong></div>
          </div>

          {shown.length === 0 ? (
            <div className="card" style={{ color: 'var(--text-3)', fontSize: 13, marginTop: 'var(--sp-4)' }}>{t('adminPublishers.empty')}</div>
          ) : (
            <div style={{ display: 'grid', gap: 'var(--sp-3)', marginTop: 'var(--sp-4)' }}>
              {shown.map((p) => {
                const isOpen = open === p.id;
                const avatar = p.avatarUrl ? `${API_URL}${p.avatarUrl}` : null;
                return (
                  <div key={p.id} className="card" style={{ padding: 0 }}>
                    <button
                      onClick={() => setOpen(isOpen ? null : p.id)}
                      style={{ width: '100%', background: 'transparent', border: 0, cursor: 'pointer', padding: 'var(--sp-4)', display: 'flex', gap: 14, alignItems: 'center', textAlign: 'left', color: 'inherit' }}
                    >
                      <span style={{ width: 40, height: 40, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, background: 'var(--surface-2)', display: 'grid', placeItems: 'center' }}>
                        {avatar
                          ? <img src={avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : <Icon name="video" size={16} />}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          {p.displayName || '—'}
                          {(p.platforms?.length ? p.platforms : [p.platform].filter(Boolean)).map((pf) => (
                            <span key={pf} className="badge" style={{ fontSize: 11 }}>{PLATFORM_LABEL[pf] || pf}</span>
                          ))}
                          {p.termsAcceptedAt
                            ? <span className="badge ok" style={{ fontSize: 11 }}>{t('adminPublishers.terms_ok')}</span>
                            : <span className="badge crit" style={{ fontSize: 11 }}>{t('adminPublishers.terms_no')}</span>}
                        </div>
                        <div style={{ color: 'var(--text-3)', fontSize: 12.5, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {p.email}{p.fullName ? ` · ${p.fullName}` : ''} · {t('adminPublishers.joined')} {date(p.createdAt)}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexShrink: 0, fontSize: 12.5, color: 'var(--text-2)' }}>
                        <span title={t('adminPublishers.sum_links')}><strong style={{ color: 'var(--text-1)' }}>{p.totals.links}</strong> {t('adminPublishers.links_short')}</span>
                        <span title={t('adminPublishers.sum_clicks')}><strong style={{ color: 'var(--text-1)' }}>{p.totals.clicks}</strong> {t('adminPublishers.clicks_short')}</span>
                        <span title={t('adminPublishers.sum_referred')}><strong style={{ color: 'var(--text-1)' }}>{p.totals.referred}</strong> {t('adminPublishers.referred_short')}</span>
                        <Icon name="arrow" size={14} style={{ transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
                      </div>
                    </button>

                    {isOpen && (
                      <div style={{ borderTop: '1px solid var(--border)', padding: 'var(--sp-4)', display: 'grid', gap: 'var(--sp-5)' }}>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 12.5 }}>
                          <span style={{ color: 'var(--text-3)' }}>{t('adminPublishers.referral_code')}:</span>
                          <code>{p.referralCode}</code>
                          <span style={{ color: 'var(--text-3)' }}>·</span>
                          <a href={p.referralUrl} target="_blank" rel="noreferrer">{p.referralUrl}</a>
                        </div>

                        <div>
                          <div className="nav-group-label" style={{ marginTop: 0 }}>{t('adminPublishers.links_title')}</div>
                          {p.links.length === 0 ? (
                            <p style={{ color: 'var(--text-3)', fontSize: 12.5 }}>{t('adminPublishers.no_links')}</p>
                          ) : (
                            <div style={{ overflowX: 'auto' }}>
                              <table className="ag-table">
                                <thead><tr>
                                  <th>{t('adminPublishers.col_label')}</th>
                                  <th>{t('adminPublishers.col_kind')}</th>
                                  <th>{t('adminPublishers.col_tracking')}</th>
                                  <th>{t('adminPublishers.col_target')}</th>
                                  <th>{t('adminPublishers.col_clicks')}</th>
                                  <th>{t('adminPublishers.col_created')}</th>
                                </tr></thead>
                                <tbody>
                                  {p.links.map((l) => (
                                    <tr key={l.id}>
                                      <td>{l.label || t('adminPublishers.untitled')}</td>
                                      <td>{t(`adminPublishers.kind_${l.kind}`, { defaultValue: l.kind })}</td>
                                      <td><a href={l.trackingUrl} target="_blank" rel="noreferrer">/y/{l.token}</a></td>
                                      <td style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.targetUrl}>{l.targetUrl}</td>
                                      <td>
                                        <strong>{l.clicks}</strong>
                                        {l.sources && Object.keys(l.sources).length > 0 && (
                                          <div style={{ fontSize: 10.5, color: 'var(--text-3)' }}>
                                            {['youtube', 'tiktok', 'twitch', 'kick', 'instagram', 'other', 'unknown']
                                              .filter((s) => l.sources[s])
                                              .map((s) => `${s === 'unknown' ? '?' : PLATFORM_LABEL[s] || s} ${l.sources[s]}`)
                                              .join(' · ')}
                                          </div>
                                        )}
                                      </td>
                                      <td style={{ whiteSpace: 'nowrap' }}>{date(l.createdAt)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>

                        <div>
                          <div className="nav-group-label" style={{ marginTop: 0 }}>{t('adminPublishers.referrals_title')}</div>
                          {p.referrals.length === 0 ? (
                            <p style={{ color: 'var(--text-3)', fontSize: 12.5 }}>{t('adminPublishers.no_referrals')}</p>
                          ) : (
                            <div style={{ overflowX: 'auto' }}>
                              <table className="ag-table">
                                <thead><tr>
                                  <th>{t('adminPublishers.col_owner')}</th>
                                  <th>{t('adminPublishers.col_plan')}</th>
                                  <th>{t('adminPublishers.col_status')}</th>
                                  <th>{t('adminPublishers.col_referred_at')}</th>
                                </tr></thead>
                                <tbody>
                                  {p.referrals.map((r) => (
                                    <tr key={r.userId}>
                                      <td>{r.email
                                        ? <Link to={`/admin/customers/${r.userId}`}>{r.displayName || r.email}</Link>
                                        : <span style={{ color: 'var(--text-3)' }}>{r.userId}</span>}</td>
                                      <td>{r.subPlan || '—'}</td>
                                      <td>{r.subStatus
                                        ? <span className={`badge ${r.subStatus === 'active' ? 'ok' : ''}`} style={{ fontSize: 11 }}>{r.subStatus}</span>
                                        : '—'}</td>
                                      <td style={{ whiteSpace: 'nowrap' }}>{date(r.referredAt)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </DashboardLayout>
  );
}
